import { Subject } from "rxjs";
import { Theme } from 'rainbow-web-sdk/models/common/theme';

/**
 * User Profile Feature Customisation Keys.
 *
 * @see 'Rainbow application customisation' section at {@link https://api.openrainbow.org/admin/#api-companies}.
 */
type UserProfileFeatureCustomizationProperty
    = 'fileSharingCustomisation' // Activate/Deactivate file sharing capability per company, per user.
    | 'userTitleNameCustomisation' // Activate/Deactivate the capability for a user to modify a part of his profile (title, firstName, lastName).
    | 'useRoomCustomisation' // Activate/Deactivate the capability for a user to use bubbles.
    | 'phoneMeetingCustomisation' // Activate/Deactivate the capability for a user to use phone meetings (PSTN conference).
    | 'useChannelCustomisation' // Activate/Deactivate the capability for a user to use a channel.
    | 'useScreenSharingCustomisation' // Activate/Deactivate the capability for a user to share a screen.
    | 'useWebRTCVideoCustomisation' // Activate/Deactivate the capability for a user to switch to a Web RTC video conversation.
    | 'useWebRTCAudioCustomisation' // Activate/Deactivate the capability for a user to switch to a Web RTC audio conversation.
    | 'useWebRTCOnlyIfMobileLoggedCustomisation' // Activate/Deactivate the capability for a user to receive web RTC call if mobile app is signed in.
    | 'instantMessagesCustomisation' // Activate/Deactivate the capability for a user to use and receive instant messages.
    | 'userProfileCustomisation' // Activate/Deactivate the capability for a user to modify globally his profile and not only (title, firstName, lastName).
    | 'fileStorageCustomisation' // Activate/Deactivate the capability for a user to access to the Rainbow file storage.
    | 'overridePresenceCustomisation' // Activate/Deactivate the capability for a user to change manually his presence status.
    | 'changeTelephonyCustomisation' // Activate/Deactivate the ability for a user to modify telephony settings like forward activation.
    | 'changeSettingsCustomisation' // Activate/Deactivate the ability for a user to change all client general settings.
    | 'recordingConversationCustomisation' // Activate/Deactivate the capability for a user to record a conversation.
    | 'useGifCustomisation' // Activate/Deactivate the ability for a user to Use GIFs in conversations.
    // 'fileCopyCustomisation' | // UNUSED - Activate/Deactivate the capability for a user to copy any file he receives in his personal cloud space.
    | 'fileTransferCustomisation' // Activate/Deactivate the capability for a user to copy a file from a conversation then share it inside another conversation.
    // 'forbidFileOwnerChangeCustomisation' | // UNUSED - Activate/Deactivate the ability for a user to loose the ownership on one file.
    | 'readReceiptsCustomisation' // Activate/Deactivate the capability for a user to allow a sender to check if a chat message is read.
    | 'useSpeakingTimeStatistics' // Activate/Deactivate the ability for a user to see speaking time statistics..
    | 'eLearningCustomisation' // Activate/Deactivate the ability for a user to participate on an Elearning training.
    | 'eLearningGamificationCustomisation' // Activate/Deactivate the ability for a user to earn badges for Elearning progress.
    | 'meetingRecordingCustomisation' // Activate/Deactivate the ability for a user to record a meeting.
    | 'useOtherPhoneMode' // Activate/Deactivate the ability for a user to be on other phone mode.
    | 'useComputerMode' // Activate/Deactivate the ability for a user to be on computer mode.
    | 'useSoftPhoneMode' // Activate/Deactivate the ability for a user to be on softphone mode.
    | 'useTeamsMode' // Activate/Deactivate the ability for a user to be on Teams mode.
    | 'imPopupDuration' // Defines the IM popup duration.
    | 'canAccessWhatsNew' // Activate/Deactivate the ability for a user to access to what's new.
    | 'canAccessFaqCustomisation' // Activate/Deactivate the ability for a user to access to FAQ.
    | 'canAccessHelpCenterCustomisation' // Activate/Deactivate the ability for a user to access to help center.
    | 'canAccessStoreCustomisation' // Activate/Deactivate the ability for a user to access to Rainbow store.
    | 'canDownloadAppCustomisation' // Activate/Deactivate the ability for a user to download Rainbow application.
    | 'canUseTestConfigCustomisation' // Activate/Deactivate the ability for a user to test the configuration.
    | 'canUseSendReportCustomisation' // Activate/Deactivate the ability for a user to send report feature.
    | 'canUseTaskCustomisation' // Activate/Deactivate the ability for a user to create and use tasks.
    | 'canCallParticipantPbxNumberCustomisation' // Select the kind of number with what a user can call a participant.
    | 'canSetInvisiblePresenceCustomisation' // Activate/Deactivate the ability for a user to set invisible presence.
    | 'useDialOutCustomisation' // Activate/Deactivate the capability for a user to use dial out in phone meetings.
    | 'receivedFileCustomisation' // Activate/Deactivate the ability for a user to restrict the receiving of files to protect from data coming from a foreign user of the company
    | 'userAvatarCustomisation' // Activate/Deactivate the ability for a user to add or change avatar.
    | 'canSynchCalendar' // Activate/Deactivate the ability for a user to synchronize the calendar
    | 'canUseTranscriptionCustomisation' // Activate/Deactivate the ability for a user to enable transcript in a meeting
    | 'hidePresenceDurationCustomisation' // Activate/Deactivate the absence of the user's Offline/Away presence duration,
    | 'canSetDNDinTeams' // Activate/Deactivate the ability for a user to set DND on Rainbow phone call.
    | 'showFirstPhoneCalls'; // Activate/Deactivate the ability for a user to change the order between rainbow calls and phone calls.
/**
 * Interface that represents the licensing status of a Rainbow Edge.
 *
 * @public
 */
export interface IEdgeStatus {

    /**
     * Edge status.
     *
     * @readonly
     */
    readonly status: 'init' | 'active' | 'warning' | 'error';

    /**
     * Edge MAC address.
     *
     * @readonly
     */
    readonly macAdress: string;

    /**
     * Edge uuid.
     *
     * @readonly
     */
    readonly uuid: string;

    /**
     * Edge activation date.
     */
    readonly activationDate: string;
}

/**
 * Interface that represents the information related to a company.
 * Depending on contact type, by 'company' we mean
 * - For a rainbow user, the name of the company to which he belong.
 *   In this case, the company is a entity managed by administrator(s)
 * - For a personal or business directory contact, the name of a company indicated for information purposes.
 * - For a O365 contact owned by a rainbow user, the name of the company to which the user belong.
 * Depending on the type of contact, not all properties may be present.
 */
export interface CompanyInfo {
    /**
     * The company unique identifier.
    *  Information only available only for a rainbow user.
     * @readonly
     * @internal
     */
    id?: string;

    /**
     * The company name.
     * @readonly
     */
    name: string;

    /**
     * The company slogan.
     * @readonly
     */
    slogan?: string;

    logo?: string;

    lastAvatarUpdateDate?: any;

    companyFeatures?: any;
}

//********************************************************//
//** OFFICE 365                                         **//
//********************************************************//

/**
 * @internal
 * List of modes that specify how activation of O365 calendar and teams presence synchronization are handled for company's users.
 * Available only for the connected user and when Office365 is configured on admin side.
 */
export enum Office365AdminMode {

    /**
     * The user should grant authorization and activate synchronization to their calendar and teams presence by himself.
     */
    USER_CONSENT = 'user_consent',

    /**
     * Admin has granted authorization but let all users to activate synchronization to their calendar and teams presence.
     */
    ADMIN_CONSENT = 'admin_consent',

    /**
     * Admin has granted authorization but all users are triggered each day to activate synchronization
     * to their calendar and teams presence.
     */
    USER_DELEGATED = 'user_delegated',

    /**
     * Admin has granted authorization and subcribe all users limited to 650
     * in order to synchronize them to calendar and teams presence.
     *
     * @remarks Only allowed if {@link Company.isOffice365AdminDelegatedModeAuthorized} is true
     */
    ADMIN_DELEGATED = 'admin_delegated',

    /**
     * Admin has granted authorization and subcribe all users in order to synchronize them to calendar.
     * Only Rainbow presence is synchornized to teams.
     */
    APPLICATION = 'application'
}

/**
 *      This class is used to represent a company in Rainbow.
 *      A company is a set of Rainbow users grouped in that entity.
 *      A company is managed by one or several administrators.
 *      A company can be visible by users that are not inside this company of not.
 */
export class Company {

    /**
     * Value can be:<br>
     * - 'initializing': When the company is initializing,<br>
     * - 'active': When the company is active,<br>
     * - 'terminated': When the company is closed
     */
    public static StatusValues = ["initializing", "active", "alerting", "hold", "terminated"];

    /**
     * Value can be:
     * - 'private': When the users of this company are not visible from an external user,<br>
     * - 'organization': When the users of this company are only visible from an extenal user of the same organization,<br>
     * - 'public': When the users of this company are visible from an external user
     */
    public static VisibilityValuesWithOrganization = ["public", "private", "organization", "closed", "isolated"];
    public static VisibilityValues = ["public", "private", "closed", "isolated"];

    /**
     * Value can be:
     * - 'noAnalytics'
     * - 'companyLevel'
     * - 'conversationLevel'
     */
    public static PrivacyValues = ["no_analytics", "company_level", "user_level"];
    // conversation_level: TODO when available

    public static AnonymizationLevels = ["full_anonymization", "on_demand_deanonymization", "no_anonymization"];

    /**
     * Value can be:
     * - 'self-employed': For a single person company,
     * - '1-10 employees': For a company with 1 to 10 employees,
     * - "51-200 employees": For a company with 51 to 200 employees,
     * - "201-500 employees": For a company with 201 to 500 employees,
     * - "501-1000 employees": For a company with 501 to 1000 employees,
     * - "1001-5000 employees": For a company with 1001 to 5000 employees,
     * - "5001-10,000 employees": For a company with 5001 to 10,000 employees,
     * - "10,001+ employees": For a company with more than 10000 employees
     */
    public static SizeValues = {
        "self-employed": "self-employed",
        "1-10 employees": "1-10_employees",
        "11-50 employees": "11-50_employees",
        "51-200 employees": "51-200_employees",
        "201-500 employees": "201-500_employees",
        "501-1000 employees": "501-1000_employees",
        "1001-5000 employees": "1001-5000_employees",
        "5001-10,000 employees": "5001-10,000_employees",
        "10,001+ employees": "10,001+_employees"
    };

    public static OfferTypes = [
        /** Freemium company */
        "freemium",
        /** Premium company */
        "premium"
    ];

    /**
     * Value can be:<br>
     * - 'VAD': For Business Partner who is a  Value Added Distributor,<br>
     * - 'DR': For Business Partner who is a Direct Reseller,<br>
     * - 'IR': For Business Partner who is an Indirect Reseller
     */
    public static BPTypes = ["VAD", "DR", "IR"];

    public static BPTypeLabels = { IR: "Indirect Reseller", VAD: "Value Added Distributor", DR: "Direct Reseller" };

    public static BPBusinessModels = [
        /** Business Parner model is 'Resell' */
        "resell"
    ];

    public static BPBusinessModelLabels = { resell: "Resell", referral: "Referral" };

    /**
     * Value can be:
     * - 'all': Admin of this company is allowed to downgrade and upgrade the number of all subscriptions (default),
     * - 'increase_only': Admin of this company is only allowed to increase the number of all subscriptions,
     * - 'monthly': Admin of this company is downgrade and upgrade the number of monthly subscriptions only (ie. not prepaid).
     */
    public static adminAllowedUpdateSubscriptionsOperations = ["all", "increase_only", "monthly"];

    public static BPBusinessTypes = {
        DEFAULT: "default",
        VOICE_BY_ALE: "voice_by_ale",
        VOICE_BY_PARTNER: "voice_by_partner",
        CONFERENCE: "conference",
        RAINBOW_EDGE: "rainbow_edge"
    };

    public static EconomicActivityClassificationTypes = {
        /** Not defined */
        NONE: "notDefined-f",
        /** Agriculture based activity */
        A: "agriculture",
        /** Mining based activity */
        B: "mining",
        /** Manifacturing based activity */
        C: "manufacturing",
        /** Electricity based activity */
        D: "electricity",
        /** Water supply based activity */
        E: "water_supply",
        /** Construction based activity */
        F: "construction",
        /** Wholesale based activity */
        G: "wholesale",
        /** Transportation based activity */
        H: "transportation",
        /** Accomodation based activity */
        I: "accommodation",
        /** Information and communication based activity */
        J: "information_and_communication",
        /** Financial based activity */
        K: "financial",
        /** Real estate based activity */
        L: "real_estate_activities",
        /** Professional based activity */
        M: "professional",
        /** Administrative based activity */
        N: "administrative",
        /** Public administration based activity */
        O: "public_administration",
        /** Education based activity */
        P: "education",
        /** Human health based activity */
        Q: "human_health",
        /** Arts based activity */
        R: "arts",
        /** Other service based activity */
        S: "other_service_activities",
        /** Activities of households as employer based activity */
        T: "activities_of_households_as_employer",
        /** Activities of extraterritorial organisations and bodies based activity */
        U: "activities_of_extraterritorial_organisations_and_bodies"
    };

    /**
     * Rainbow application customisation
     */
    private static userProfileAttributes: { name: UserProfileFeatureCustomizationProperty; defaultValue: 'enabled' | 'disabled' | number | undefined; }[] = [
        { name: "instantMessagesCustomisation", defaultValue: "enabled" },
        { name: "useGifCustomisation", defaultValue: "enabled" },
        { name: "fileSharingCustomisation", defaultValue: "enabled", },
        { name: "fileStorageCustomisation", defaultValue: "enabled" },
        { name: "phoneMeetingCustomisation", defaultValue: "enabled" },
        { name: "useDialOutCustomisation", defaultValue: "enabled" },
        { name: "useChannelCustomisation", defaultValue: "enabled" },
        { name: "useRoomCustomisation", defaultValue: "enabled" },
        { name: "useScreenSharingCustomisation", defaultValue: "enabled" },
        { name: "useWebRTCAudioCustomisation", defaultValue: "enabled" },
        { name: "useWebRTCOnlyIfMobileLoggedCustomisation", defaultValue: "disabled" },
        { name: "useWebRTCVideoCustomisation", defaultValue: "enabled" },
        { name: "recordingConversationCustomisation", defaultValue: "enabled" },
        { name: "overridePresenceCustomisation", defaultValue: "enabled" },
        { name: "userProfileCustomisation", defaultValue: "enabled" },
        { name: "userTitleNameCustomisation", defaultValue: undefined },
        { name: "changeTelephonyCustomisation", defaultValue: "enabled" },
        { name: "changeSettingsCustomisation", defaultValue: "enabled" },
        { name: "recordingConversationCustomisation", defaultValue: "enabled" },
        // fileCopyCustomisation ?
        { name: "fileTransferCustomisation", defaultValue: "enabled", },
        // forbidFileOwnerChangeCustomisation ?
        { name: "readReceiptsCustomisation", defaultValue: "enabled" },
        { name: "useSpeakingTimeStatistics", defaultValue: "enabled" },
        { name: "eLearningCustomisation", defaultValue: "enabled" },
        { name: "eLearningGamificationCustomisation", defaultValue: "enabled" },
        { name: "meetingRecordingCustomisation", defaultValue: "enabled" },
        { name: "useOtherPhoneMode", defaultValue: "enabled" },
        { name: "useComputerMode", defaultValue: "enabled" },
        { name: "useSoftPhoneMode", defaultValue: "disabled" },
        { name: "useTeamsMode", defaultValue: "disabled" },
        { name: "imPopupDuration", defaultValue: 3 },
        { name: "canAccessWhatsNew", defaultValue: "enabled" },
        { name: "canAccessFaqCustomisation", defaultValue: "enabled" },
        { name: "canAccessHelpCenterCustomisation", defaultValue: "enabled" },
        { name: "canAccessStoreCustomisation", defaultValue: "enabled" },
        { name: "canDownloadAppCustomisation", defaultValue: "enabled" },
        { name: "canUseTestConfigCustomisation", defaultValue: "enabled" },
        { name: "canUseSendReportCustomisation", defaultValue: "enabled" },
        { name: "canUseTaskCustomisation", defaultValue: "enabled" },
        { name: "canCallParticipantPbxNumberCustomisation", defaultValue: "enabled" },
        { name: "canSetInvisiblePresenceCustomisation", defaultValue: "enabled" },
        { name: "receivedFileCustomisation", defaultValue: "enabled" },
        { name: "userAvatarCustomisation", defaultValue: "enabled" },
        { name: "canSynchCalendar", defaultValue: "enabled" },
        { name: "canUseTranscriptionCustomisation", defaultValue: "disabled" },
        { name: "hidePresenceDurationCustomisation", defaultValue: "disabled" },
        { name: "canSetDNDinTeams", defaultValue: "enabled" },
        { name: "showFirstPhoneCalls", defaultValue: "disabled" }
    ];

    private static attributes = [
        { name: "id", defaultValue: undefined },
        { name: "name", defaultValue: "" },
        { name: "creationDate", defaultValue: undefined },
        { name: "companyContactId", defaultValue: undefined },
        { name: "adminEmail", defaultValue: "" },
        { name: "supportEmail", defaultValue: undefined },
        { name: "status", defaultValue: "active" },
        { name: "economicActivityClassification", defaultValue: "NONE" },
        { name: "website", defaultValue: undefined },
        { name: "description", defaultValue: undefined },
        { name: "visibility", defaultValue: "private" },
        { name: "visibleBy", defaultValue: [] },
        { name: "organisationId", defaultValue: "" },
        { name: "userSelfRegisterEnabled", defaultValue: false },
        { name: "userSelfRegisterAllowedDomains", defaultValue: [] },
        { name: "offerType", defaultValue: "freemium" },
        { name: "street", defaultValue: undefined },
        { name: "city", defaultValue: undefined },
        { name: "postalCode", defaultValue: undefined },
        { name: "country", defaultValue: undefined },
        { name: "state", defaultValue: null },
        { name: "timezone", defaultValue: undefined },
        { name: "size", defaultValue: undefined },
        { name: "slogan", defaultValue: undefined },
        { name: "isBP", defaultValue: false },
        { name: "bpType", defaultValue: undefined },
        { name: "bpBusinessModel", defaultValue: undefined },
        { name: "bpApplicantNumber", defaultValue: undefined },
        { name: "bpCRDid", defaultValue: undefined },
        { name: "bpHasRightToSell", defaultValue: undefined },
        { name: "bpId", defaultValue: undefined },
        { name: "bpHasRightToConnect", defaultValue: undefined },
        { name: "adminHasRightToUpdateSubscriptions", defaultValue: false },
        { name: "adminAllowedUpdateSubscriptionsOps", defaultValue: "all" },
        { name: "sendPrepaidSubscriptionsNotification", defaultValue: undefined },
        { name: "externalReference", defaultValue: undefined },
        { name: "externalReference2", defaultValue: undefined },
        { name: "lastAvatarUpdateDate", defaultValue: null },
        { name: "lastBannerUpdateDate", defaultValue: null },
        { name: "avatarShape", defaultValue: "circle" },
        { name: "catalogId", defaultValue: undefined },
        { name: "office365Tenant", defaultValue: undefined },
        { name: "office365ScopesGranted", defaultValue: undefined },
        { name: "isOffice365AdminDelegatedModeAuthorized", defaultValue: undefined },
        { name: "office365AdminMode", defaultValue: undefined },
        { name: "office365SynchronizeCalendarPresence", defaultValue: undefined },
        { name: "office365SynchronizeTeamsPresence", defaultValue: undefined },
        { name: "isCentrex", defaultValue: undefined },
        { name: "tenantCallNumber", defaultValue: undefined },
        { name: "tenantName", defaultValue: undefined },
        { name: "numberUsers", defaultValue: undefined },
        { name: "dataLocation", defaultValue: undefined },
        { name: "businessSpecific", defaultValue: undefined },
        { name: "superadminComment", defaultValue: undefined }, // Free field that only superadmin can use.
        { name: "bpBusinessType", defaultValue: [] },
        { name: "voIPSettings", defaultValue: undefined, },
        { name: "ddiReadOnly", defaultValue: undefined, },

        { name: "allowUsersSelectTheme", defaultValue: true, },
        { name: "allowUsersSelectPublicTheme", defaultValue: true, },
        { name: "selectedTheme", defaultValue: undefined, },
        { name: "selectedThemeCustomers", defaultValue: undefined, },

        { name: "selectedAppCustomisationTemplate", defaultValue: undefined },
        { name: "mobilePermanentConnectionMode", defaultValue: undefined },
        { name: "alertNotificationSending", defaultValue: "disabled" },
        { name: "alertNotificationReception", defaultValue: "disabled" },
        { name: "cloudPbxRecordingInboundOnly", defaultValue: undefined },
        { name: "defaultLicenseGroup", defaultValue: undefined },
        { name: "defaultOptionsGroups", defaultValue: undefined },
        { name: "allowDeviceFirmwareSelection", defaultValue: false },
        { name: "selectedDeviceFirmware", defaultValue: "released" },
        { name: "supportUrlFAQ", defaultValue: undefined },
        { name: "allowPhoneNumbersVisibility", defaultValue: undefined },
        { name: "disableCCareAdminAccess", defaultValue: undefined },
        { name: "disableCCareAdminAccessCustomers", defaultValue: undefined },
        { name: "disableCCareAdminAccessResellers", defaultValue: undefined },
        { name: "cloudPbxVoicemailToEmail", defaultValue: "none" },
        { name: "supervisionGroupMaxNumber", defaultValue: 5 },
        { name: "supervisionGroupMaxSize", defaultValue: 1500 },
        { name: "supervisionGroupMaxUsers", defaultValue: 30 },
        { name: "supervisionGroupMaxNumberForCompany", defaultValue: 200 }, //RQRAINB-12981
        { name: "autoAcceptUserInvitations", defaultValue: "true" },
        { name: "autoAddToUserNetwork", defaultValue: "false" },
        { name: "mainStorage", defaultValue: "Rainbow Storage" },
        { name: "useRainbowStorage", defaultValue: "enabled" },
        { name: "useExternalStorage", defaultValue: "disabled" },
        { name: "bpHasRightForBYOT", defaultValue: false },
        { name: "contentPolicyLifeTime", defaultValue: -1 },
        { name: "documentGracePeriod", defaultValue: 14400 },
        { name: "adminServiceNotificationsLevel", defaultValue: "high" },
        { name: "endOfConferenceBehavior", defaultValue: undefined },
        { name: "teamsPresenceOnRainbowBusyPhone", defaultValue: 'Busy' },
        { name: "hideContractInfo", defaultValue: undefined },
        { name: "displayIdrFiles", defaultValue: undefined },
        { name: "delegatedCallPushDisable", defaultValue: true },
        { name: "isEdge", defaultValue: false },
        { name: "edgeData", defaultValue: undefined },
        { name: "adminHasTheRightToEnableDeskphoneFailover", defaultValue: false },
        { name: "selectedSplashScreen", defaultValue: undefined },
        { name: "allowCloudPbxDeviceAccess", defaultValue: false },
        { name: "enableNewVirusScanPolicy", defaultValue: false },
        { name: "referrals", defaultValue: [] },
        ...Company.userProfileAttributes

    ];

    /**
     * @param id - Id of the company
     * @param name - Name of the company
     * @returns the created company
     */
    public static create(id: string, name: string): Company {
        return new Company({ id: id, name: name });
    }

    /**
     * @param data - The object containing the attributes of the company
     * @returns the created company
     */
    public static createFromData(data: any): Company {
        const company = new Company(data);

        // Handle fields for themes management
        if (!company.selectedThemeCustomers) company.selectedThemeCustomers = {};
        if (!company.selectedTheme) company.selectedTheme = {};
        // Handle fields for splash screen management
        if (!company.selectedSplashScreen) company.selectedSplashScreen = {};

        return company;
    }

    /**
     * Returns a copy of the company's data but expurgated by the unwanted and undefined optional properties.
     *
     * @param data - The object containing the attributes of the company
     * @param userContact - The logged user
     * @returns the updated company
     */
    public static prune(data: any, userContact: any): Company {
        const dataCopy = Object.assign({}, data);

        // Remove readonly fields
        delete dataCopy.dataLocation;
        delete dataCopy.businessSpecific;

        // Remove readonly fields for themes management
        if (dataCopy.selectedTheme && Object.prototype.hasOwnProperty.call(dataCopy.selectedTheme, "isFromBp")) {
            // Serveur doesn't accept this parameter in update so remove it!
            delete dataCopy.selectedTheme.isFromBp;
        }
        if (dataCopy.selectedThemeCustomers && Object.prototype.hasOwnProperty.call(dataCopy.selectedThemeCustomers, "isFromBp")) {
            // Serveur doesn't accept this parameter in update so remove it!
            delete dataCopy.selectedThemeCustomers.isFromBp;
        }

        // Remove deprecated fields
        delete dataCopy.giphyEnabled; // 'useGifCustomisation' is used instead

        // Remove unnecessary fields or inconsistent with other fields
        // Note: if the property bpId is null, we send it (used when bpType changes from IR to DR or VAD)
        if (dataCopy.isBP) {
            if (dataCopy.bpType !== "VAD" && dataCopy.bpType !== "DR") {
                delete dataCopy.bpBusinessType; // cannot be set when not a VAD or DR
            }
        } else {
            // these fields are only applicable if isBP is true.
            delete dataCopy.bpType;
            delete dataCopy.bpBusinessModel;
            delete dataCopy.bpApplicantNumber;
            delete dataCopy.bpCRDid;
            delete dataCopy.bpHasRightToSell;
            delete dataCopy.bpHasRightToConnect;
            delete dataCopy.bpIsContractAccepted;
        }
        if (dataCopy.economicActivityClassification === "") {
            delete dataCopy.economicActivityClassification;
        }

        // Remove fields for which the authorization level is not reached
        if (!dataCopy.allowDeviceFirmwareSelection) {
            delete dataCopy.selectedDeviceFirmware;
        }

        if (!userContact || (!userContact.isSuperadmin() && !userContact.isBPAdminFinance())) {
            // these fields can't be set if the user is not a "superadmin" or a "bp_finance".
            delete dataCopy.bpType;
            delete dataCopy.adminHasRightToUpdateSubscriptions;
            delete dataCopy.adminAllowedUpdateSubscriptionsOps;
            delete dataCopy.sendPrepaidSubscriptionsNotification;
        }
        if (!userContact?.isSuperadmin()) {
            // these fields can't be set if the user is not a "superadmin"
            delete dataCopy.superadminComment;
            delete dataCopy.allowDeviceFirmwareSelection;
        }
        if (!userContact || (!userContact.isSuperadmin() && (dataCopy.supportEmail === null || dataCopy.supportEmail === ""))) {
            delete dataCopy.supportEmail;
        }

        return dataCopy;
    }

    public id: string;

    public name: string;

    public status: string;

    public state: string;

    public logo: any = null;

    public nameForLogs: string = "";

    public filterName: string;

    public offerType: string;
    public isBP: boolean;
    public bpId: string;
    public bpType: string;
    public bpBusinessType: string[];

    private rxSubject: Subject<any>;

    public banner: any = null;
    public avatarShape: string;
    public slogan: string;
    public country: any;
    public timezone: string;
    public economicActivityClassification: any;
    public size: any;
    public website: string;
    public companyContactId: string;
    public description: string;
    public supportEmail: string;
    public lastAvatarUpdateDate: any;
    public lastBannerUpdateDate: any;
    public selectedSplashScreen: { lastUpdateDate?: string; isForced?: boolean; uploadedUsed?: boolean; companyId?: string; };

    public visibility: string = 'public';
    public numberUsers: number;
    public organisationId: string;

    //********************************************************//
    //** OFFICE 365 tenant                                  **//
    //********************************************************//

    /**
     * Office365 tenant configured for this company.
     */
    public office365Tenant: string;

    /**
     * Scopes granted to Rainbow for usage of Microsoft Office365 APIs.
     *
     * Non-exhaustive list of values : 'directory' | 'calendar' | 'provisioning' | 'meeting_scheduler' | 'o365_admin' | 'teams' | 'personal_contacts'
     */
    public office365ScopesGranted: string[] = [];

    //********************************************************//
    //** OFFICE 365 consent for Presence/calendar           **//
    //********************************************************//

    /**
     * Indicates if the admin delegated mode could be activated (ie. 'office365AdminMode' property).
     */
    public readonly isOffice365AdminDelegatedModeAuthorized: boolean;

    /**
     * Specifies how activation of O365 calendar and teams presence synchronization are handled for company's users.
     */
    public office365AdminMode: Office365AdminMode;

    /**
     * Indicates if all users should be synchronized with their office365 Calendar presence.
     */
    public office365SynchronizeCalendarPresence: boolean;

    /**
     * Indicates if all users should be synchronized with their office365 Teams presence.
     */
    public office365SynchronizeTeamsPresence: boolean;

    // BP parameters
    public bpHasRightToConnect: boolean;
    public cloudPbxRecordingInboundOnly: boolean;
    public hideContractInfo: boolean;
    public displayIdrFiles: boolean; // Indicates if IDR files should be visible to BP (invoices view)

    // Theme parameters
    public allowUsersSelectTheme: boolean;
    public allowUsersSelectPublicTheme: boolean;
    public selectedTheme: any;
    public originalSelectedTheme: any;
    public selectedThemeCustomers: any;
    public originalSelectedThemeCustomers: any;

    public allowDeviceFirmwareSelection: boolean;
    public selectedDeviceFirmware: string;
    public allowCloudPbxDeviceAccess: boolean;

    // Edge company parameters
    public isEdge: boolean;
    public edgeData: IEdgeStatus;

    // Privacy
    public privacy: any;

    public dataLocation: any;

    public supportUrlFAQ: string;

    public disableCCareAdminAccess: boolean;

    public disableCCareAdminAccessCustomers: boolean;

    public disableCCareAdminAccessResellers: boolean;

    public adminHasRightToUpdateSubscriptions: boolean;
    public adminAllowedUpdateSubscriptionsOps: string;
    public sendPrepaidSubscriptionsNotification: boolean;
    public adminHasTheRightToEnableDeskphoneFailover: boolean;

    // Level Notification
    public adminServiceNotificationsLevel: string;

    public teamsPresenceOnRainbowBusyPhone: string;

    public endOfConferenceBehavior: {
        behavior: 'rainbow' | 'custom_url' | 'no_redirect';
        customUrl?: string;
    };

    public enableNewVirusScanPolicy: boolean;

    // Custom manifest allowed domains
    public referrals: string[];

    constructor(data: any) {
        // Generic initialization of attributes values from received data or default values
        Company.attributes.forEach((attribute) => {
            if (data[attribute.name] !== undefined) {
                // The attribute is present in the received data object : we take the value received in data object
                this[attribute.name] = data[attribute.name];
            }
            else if (attribute.defaultValue !== undefined) {
                // The attribute is not present in the received data object, and this attribute has a defined default value
                // We take the defined default value
                this[attribute.name] = attribute.defaultValue;
            }
        });

        this.state = data.state ? data.state : data.country === "USA" ? "AL" : data.country === "CAN" ? "AB" : null;
        if (this.name) { this.filterName = this.name.toLowerCase(); }
        this.rxSubject = new Subject();
    }

    /**
     * Allows to update an existing company with data from server.
     *
     * @param data - The object containing the updated attributes of the company
     */
    public updateFromData(data: any) {
        Company.attributes.forEach((attribute) => {
            if (data[attribute.name] !== undefined) {
                // The attribute is present in the received data object : we take the value received in data object
                this[attribute.name] = data[attribute.name];
            }
            // If the attribute is not present in the received data object, we don't touch the current attribute value
        });
    }

    public getNameForLogs() {
        if (!this.nameForLogs && this.name) {
            this.nameForLogs = this.name.charAt(0) + this.name.replace(/[^\s](?=.{1,}$)/g, "*").substr(1);
        }
        return this.nameForLogs;
    }

    /**
     * Gets the status of the company
     * @returns True if the company is active
     */
    public isActive(): boolean { return this.status === "active"; }
    public isInitializing(): boolean { return this.status === "initializing"; }
    public isInvited(): boolean { return this.status === "invited"; }

    /**
     * @public
     * @returns true if the company in the Freemium business model, otherwise false.
     */
    public isFreemium(): boolean { return this.offerType === "freemium"; }

    /**
     * @public
     * @returns true if the company has contracted the Premium Plan, otherwise false.
     */
    public isPremium(): boolean { return this.offerType === "premium"; }

    /**
     * @public
     * @returns true if the company is a Value Added Distributor (VAD) Business Partner company, otherwise false.
     */
    public isVAD(): boolean { return this.isBP && this.bpType === "VAD"; }

    /**
     * @public
     * @returns true if the company is an Indirect Reseller (IR) Business Partner company, otherwise false.
     */
    public isIR(): boolean { return this.isBP && this.bpType === "IR"; }

    /**
     * @public
     * @returns true if the company is a Direct Reseller (DR) Business Partner company, otherwise false.
     */
    public isDR(): boolean { return this.isBP && this.bpType === "DR"; }

    /**
     * @public
     * @returns true if the company is End Customer (EC) company, otherwise false.
     */
    public isEC(): boolean { return !this.isBP; }

    /**
     * @public
     * @returns true if the company is the Orange company, otherwise false.
     */
    public isOrange(): boolean { return this.isBP && this["bpApplicantNumber"] === "326105"; }

    /**
     * @public
     * @returns true if the company is a distributor BP is allowed to sell Rainbow Edge.
     */
    public hasRightToSellRainbowEdge(): boolean { return (this.isVAD() || this.isDR()) && this.bpBusinessType?.includes(Company.BPBusinessTypes.RAINBOW_EDGE); }

    public onChange(callback: any) { return this.rxSubject.subscribe(callback); }
    public sendChangeEvent(type: string, value: any = null): any { this.rxSubject.next({ type: type, value: value }); }

    //************************************************************//
    //** Selected Theme for current Company                      *//
    //************************************************************//

    public getSelectedWhiteThemeId(): string {
        return (this.selectedTheme) ? this.selectedTheme.light : null;
    }

    public setSelectedWhiteThemeId(lightId) {
        if (!this.selectedTheme) this.selectedTheme = {};
        this.selectedTheme.light = lightId;
    }

    public getSelectedDarkThemeId(): string {
        return (this.selectedTheme) ? this.selectedTheme.dark : null;
    }

    public setSelectedDarkThemeId(darkId) {
        if (!this.selectedTheme) this.selectedTheme = {};
        this.selectedTheme.dark = darkId;
    }

    public setSelectedTheme(theme: Theme) {
        if (theme.isWhiteType()) this.setSelectedWhiteThemeId(theme.id);
        else this.setSelectedDarkThemeId(theme.id);
    }

    //************************************************************//
    //** Selected Theme for customers Company                    *//
    //************************************************************//

    public getSelectedCustomerWhiteThemeId(): string {
        return (this.selectedThemeCustomers) ? this.selectedThemeCustomers.light : null;
    }

    public setSelectedCustomerWhiteThemeId(lightId) {
        if (!this.selectedThemeCustomers) this.selectedThemeCustomers = {};
        this.selectedThemeCustomers.light = lightId;
    }

    public getSelectedCustomerDarkThemeId(): string {
        return (this.selectedThemeCustomers) ? this.selectedThemeCustomers.dark : null;
    }

    public setSelectedCustomerDarkThemeId(darkId) {
        if (!this.selectedThemeCustomers) this.selectedThemeCustomers = {};
        this.selectedThemeCustomers.dark = darkId;
    }

    public setSelectedThemeCustomers(theme: Theme) {
        if (theme.isWhiteType()) this.setSelectedCustomerWhiteThemeId(theme.id);
        else this.setSelectedCustomerDarkThemeId(theme.id);
    }

    public toJSON(): any {
        const json: any = { id: this.id, name: this.name, country: this.country };
        if (this.state) json.state = this.state;
        return json;
    }

    //************************************************************//
    //** Privacy for current Company                             *//
    //************************************************************//

    public getPrivacy(): string {
        return this.privacy;
    }

    public setPrivacy(privacy) {
        this.privacy = privacy;
    }
}
